import { useCallback, useEffect, useMemo, useRef } from "react";
import { EVENTS, STATUS, useJoyride } from "react-joyride";
import { TOUR_IDS } from "@demandproai/shared";
import { useAddCaseFormContext } from "../contexts/AddCaseFormContext";
import { useTourContext } from "../contexts/TourContext";
import { TOUR_OPTIONS } from "../constants/tour";
import { buildEditCaseTourSteps, getEditCaseTourStops } from "../tours/editCaseTourSteps";
import { evaluateRequirement } from "../tours/editCaseTourRequirements";
import GatedTourTooltip from "../components/tour/GatedTourTooltip";
import useIsModalOpen from "./useIsModalOpen";
import useTourSpotlightSync from "./useTourSpotlightSync";
import useTourAnchorRecovery from "./useTourAnchorRecovery";

const useEditCaseTour = () => {
   const {
      formik,
      isLoading,
      addCaseFormStep,
      fromStepValue,
      presignedUrlGenerationCount,
      selectedTiffIndex,
   } = useAddCaseFormContext();
   const { registerTour, requestedTourId, clearTourRequest, isTourPending, completeTour, dismissTour } =
      useTourContext();
   const isModalOpen = useIsModalOpen();

   // Read through refs so step hooks stay stable and the steps array is not
   // rebuilt underneath a running tour
   const formStepRef = useRef(addCaseFormStep);
   const uploadCountRef = useRef(presignedUrlGenerationCount);

   useEffect(() => {
      formStepRef.current = addCaseFormStep;
   }, [addCaseFormStep]);

   useEffect(() => {
      uploadCountRef.current = presignedUrlGenerationCount;
   }, [presignedUrlGenerationCount]);

   const goToFormStep = useCallback((targetStep) => {
      const isAlreadyOnStep = targetStep === formStepRef.current;
      // The wizard rejects step changes mid upload and toasts about it
      const isBlockedByUpload = uploadCountRef.current > 0;

      if (isAlreadyOnStep || isBlockedByUpload) {
         return Promise.resolve();
      }

      fromStepValue(targetStep);

      return new Promise((resolve) => {
         requestAnimationFrame(() => resolve());
      });
   }, [fromStepValue]);

   const caseType = formik?.values?.caseInfo?.caseType;

   const steps = useMemo(
      () => buildEditCaseTourSteps({ caseType, goToFormStep }),
      [caseType, goToFormStep]
   );

   const isReplayRequested = requestedTourId === TOUR_IDS.EDIT_CASE;

   const shouldAutoRun = !isLoading && isTourPending(TOUR_IDS.EDIT_CASE);

   const handleTourEvent = useCallback(
      (data) => {
         if (data.type !== EVENTS.TOUR_END) {
            return;
         }

         if (data.status === STATUS.SKIPPED) {
            dismissTour(TOUR_IDS.EDIT_CASE);
         } else {
            completeTour(TOUR_IDS.EDIT_CASE);
         }

         clearTourRequest();
      },
      [completeTour, dismissTour, clearTourRequest]
   );

   const { Tour, controls, state, step } = useJoyride({
      steps,
      run: shouldAutoRun,
      continuous: true,
      tooltipComponent: GatedTourTooltip,
      options: TOUR_OPTIONS,
      onEvent: handleTourEvent,
   });

   useTourSpotlightSync({ step, state });
   useTourAnchorRecovery({ step, state, controls });

   const stops = useMemo(() => getEditCaseTourStops({ caseType }), [caseType]);

   const { isBlocked, hint } = evaluateRequirement({
      requirement: stops[state.index]?.requirement,
      values: formik?.values,
      selectedTiffIndex,
   });

   const skipStop = useCallback(() => controls.next(), [controls]);

   const gate = useMemo(() => ({ isBlocked, hint, skipStop }), [isBlocked, hint, skipStop]);

   // Resuming is only correct for a tour that is mid run, so this is read rather
   // than depended on: a status change must not itself trigger a pause
   const isMidRunRef = useRef(false);
   isMidRunRef.current = [STATUS.RUNNING, STATUS.PAUSED].includes(state.status);

   // Modals render below the tour overlay, so a stop carried out in one would be
   // unreachable. Pausing holds the index, unlike toggling run
   useEffect(() => {
      if (!isMidRunRef.current) {
         return;
      }

      if (isModalOpen) {
         controls.stop();
      } else {
         controls.start();
      }
   }, [isModalOpen, controls]);

   // A replay rewinds to the first stop rather than resuming where the user left off
   useEffect(() => {
      if (isReplayRequested) {
         controls.reset(true);
      }
   }, [isReplayRequested, controls]);

   useEffect(() => registerTour(TOUR_IDS.EDIT_CASE), [registerTour]);

   return { Tour, gate };
};

export default useEditCaseTour;
